import TxnTablePanel, {
  TXN_PANEL_DIALOG_CLASS,
  useTxnSort,
} from "@app/components/txn-table-panel";
import * as Dialog from "@radix-ui/react-dialog";
import { Suspense } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";

import type {
  explainDrillPanel_Query,
  PnlLineDrillEnum,
  TxnFlowEnum,
} from "@app/queries/explainDrillPanel_Query.graphql";

// The `transactions`-resolver arguments behind a calculation leaf (Types::CalcDrill).
// Passing them to the transactions query opens exactly the transactions that sum to
// the leaf's value — reconciliation is guaranteed server-side (Metrics::Explanation).
export type CalcDrill = {
  readonly pnlLine?: string | null;
  readonly flow?: string | null;
  readonly excludeDistributions?: boolean | null;
  readonly classification?: string | null;
  readonly categoryId?: string | null;
  readonly from?: string | null;
  readonly to?: string | null;
};

const drillQuery = graphql`
  query explainDrillPanel_Query(
    $pnlLine: PnlLineDrillEnum
    $flow: TxnFlowEnum
    $excludeDistributions: Boolean
    $from: String
    $to: String
    $sort: TxnSortEnum
    $sortDirection: String
  ) {
    transactions(
      pnlLine: $pnlLine
      flow: $flow
      excludeDistributions: $excludeDistributions
      from: $from
      to: $to
      sort: $sort
      sortDirection: $sortDirection
    ) {
      totalCount
      totalAmount
      limit
      txns {
        txn {
          id
        }
        md5
        postingDate
        description
        amount
        kind
        accountNumber
        internal
        classification
        counterparty {
          name
        }
        category
        expenseNature {
          id
          name
        }
      }
    }
  }
`;

// The provenance drill: the exact transactions behind a clicked number, read-only.
// Unlike the P&L cell panel this exposes no bulk actions — it answers "which
// transactions is this number made of?", not "fix them here".
export default function ExplainDrillPanel({
  title,
  drill,
  onClose,
}: {
  title: string;
  drill: CalcDrill;
  onClose: () => void;
}) {
  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/20 z-[60]" />
        {/* The shared panel class hardcodes z-50; `!z-[70]` guarantees this nested drill
            sits above the explain panel (z-50) and its own overlay (z-[60]). */}
        <Dialog.Content className={`${TXN_PANEL_DIALOG_CLASS} !z-[70]`}>
          <Suspense fallback={<div className="p-6 text-sm text-gray-400">Loading transactions…</div>}>
            <DrillContent title={title} drill={drill} onClose={onClose} />
          </Suspense>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function DrillContent({
  title,
  drill,
  onClose,
}: {
  title: string;
  drill: CalcDrill;
  onClose: () => void;
}) {
  const { sort, direction, onSort, sorting, sortVars } = useTxnSort();

  const data = useLazyLoadQuery<explainDrillPanel_Query>(
    drillQuery,
    {
      pnlLine: (drill.pnlLine ?? null) as PnlLineDrillEnum | null,
      flow: (drill.flow ?? null) as TxnFlowEnum | null,
      excludeDistributions: drill.excludeDistributions ?? null,
      from: drill.from ?? null,
      to: drill.to ?? null,
      ...sortVars,
    },
    { fetchPolicy: "store-or-network" }
  );

  // The export endpoint mirrors the panel's filter (complete set past the 500 cap).
  const exportParams: Record<string, string> = {
    sort: sortVars.sort,
    sort_direction: sortVars.sortDirection,
  };
  if (drill.pnlLine) exportParams.pnl_line = drill.pnlLine;
  if (drill.flow) exportParams.flow = drill.flow;
  if (drill.excludeDistributions) exportParams.exclude_distributions = "true";
  if (drill.from) exportParams.from = drill.from;
  if (drill.to) exportParams.to = drill.to;

  return (
    <TxnTablePanel
      title={title}
      interaction="read-only"
      totalCount={data.transactions.totalCount}
      headerTotalDollars={data.transactions.totalAmount}
      rows={data.transactions.txns}
      emptyLabel="No transactions behind this number."
      sort={sort}
      direction={direction}
      onSort={onSort} sorting={sorting}
      exportParams={exportParams}
      onClose={onClose}
    />
  );
}
